<div class="sp-page">

  <!-- Private-view banner -->
  <div *ngIf="isPrivateView" class="sp-preview-banner">
    <span>You are previewing the public status page.</span>
    <a [href]="publicStatusUrl" target="_blank" rel="noopener">Open public link &rarr;</a>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="sp-loading">
    <div class="sp-spinner"></div>
    <p>Loading status…</p>
  </div>

  <!-- Loaded -->
  <ng-container *ngIf="!loading && statusPage">

    <!-- Hero banner -->
    <header class="sp-hero"
      [class.sp-hero--ok]="overallStatus === 'ok'"
      [class.sp-hero--maintenance]="overallStatus === 'maintenance'"
      [class.sp-hero--warn]="overallStatus === 'degraded'"
      [class.sp-hero--down]="overallStatus === 'down'">
      <div class="sp-container">
        <div class="sp-hero__brand">
          <img *ngIf="statusPage.logo_url" class="sp-hero__logo" [src]="logoSrc" [alt]="statusPage.title + ' logo'"
            data-testid="sp-logo">
          <div class="sp-hero__title">{{ statusPage.title }}</div>
        </div>
        <div class="sp-hero__status-row">
          <div class="sp-hero__status">
            <span class="sp-hero__dot"></span>
            <span *ngIf="overallStatus === 'ok'">All systems operational</span>
            <span *ngIf="overallStatus === 'maintenance'">Scheduled maintenance in progress</span>
            <span *ngIf="overallStatus === 'degraded'">Some systems degraded</span>
            <span *ngIf="overallStatus === 'down'">Some systems are down</span>
          </div>
          <div class="sp-hero__refresh">
            <span class="sp-hero__refresh-dot"></span>
            <span *ngIf="lastUpdated">Updated {{ lastUpdatedLabel }} &middot; </span>Refreshing in {{ countdown }}s
          </div>
        </div>
        <p *ngIf="statusPage.description" class="sp-hero__desc">{{ statusPage.description }}</p>
      </div>
    </header>

    <!-- Components -->
    <main class="sp-container sp-main">

      <div class="sp-card" *ngFor="let c of statusPage.components">

        <div class="sp-card__header">
          <span class="sp-card__name">{{ c.name }}</span>
          <span class="sp-badge"
            [class.sp-badge--ok]="isOperational(c) && !isInMaintenance(c)"
            [class.sp-badge--maintenance]="isInMaintenance(c)"
            [class.sp-badge--warn]="!isOperational(c) && !isDown(c)"
            [class.sp-badge--down]="isDown(c)">
            <ng-container *ngIf="isInMaintenance(c)">Maintenance</ng-container>
            <ng-container *ngIf="!isInMaintenance(c) && isOperational(c)">Operational</ng-container>
            <ng-container *ngIf="isDown(c)">Down</ng-container>
            <ng-container *ngIf="!isInMaintenance(c) && !isOperational(c) && !isDown(c)">Degraded</ng-container>
          </span>
        </div>

        <p *ngIf="c.description" class="sp-card__desc">{{ c.description }}</p>

        <!-- Active maintenance: what, until when, and why -->
        <div *ngIf="c.active_maintenance as m" class="sp-maintenance-msg" data-testid="sp-active-maintenance">
          <span class="sp-maintenance-msg__icon">🔧</span>
          <div class="sp-maintenance-msg__body">
            <strong>Maintenance in progress{{ m.title ? ': ' + m.title : '' }}</strong>
            <!-- The end time is what a visitor actually wants to know; the old banner
                 said only that something was happening. -->
            <div class="sp-maintenance-msg__range">Expected back by {{ m.end_at | date:'medium' }}</div>
            <div *ngIf="m.message" class="sp-maintenance-msg__text">{{ m.message }}</div>
          </div>
        </div>

        <!-- Upcoming maintenance alerts -->
        <div *ngFor="let m of c.upcoming_maintenance" class="sp-upcoming-maintenance">
          <div class="sp-upcoming-maintenance__header">
            <span class="sp-upcoming-maintenance__icon">📅</span>
            <strong>Scheduled maintenance{{ m.title ? ': ' + m.title : '' }}</strong>
          </div>
          <div class="sp-upcoming-maintenance__range">
            {{ m.start_at | date:'medium' }} &ndash; {{ m.end_at | date:'medium' }}
          </div>
          <div *ngIf="m.message" class="sp-upcoming-maintenance__msg">{{ m.message }}</div>
        </div>

        <div class="sp-chart">
          <svg width="100%" height="34" viewBox="0 0 890 34" preserveAspectRatio="none"
               xmlns="http://www.w3.org/2000/svg">
            <g *ngFor="let bar of c.lastNinetyDaysHealthy; let i = index">
              <rect
                [attr.x]="i * 9.9"
                y="2"
                width="7"
                height="30"
                rx="3"
                ry="3"
                [attr.fill]="bar.color"
              ></rect>
              <!-- A shaded bar is only readable if it can say what the shade means. -->
              <title>{{ bar.date }}{{ bar.uptime === null ? '' : ' — ' + bar.uptime + '% uptime' }}</title>
            </g>
          </svg>
          <div class="sp-chart__labels">
            <span>90 days ago</span>
            <span>Today</span>
          </div>
        </div>

        <div class="sp-card__footer">
          <span class="sp-uptime" *ngIf="c.averageLast90Days !== null">
            {{ c.averageLast90Days }}% uptime
          </span>
          <span class="sp-uptime sp-uptime--na" *ngIf="c.averageLast90Days === null">
            No data
          </span>
        </div>

      </div>

      <!-- Legend -->
      <div class="sp-legend" data-testid="sp-legend">
        <span class="sp-legend__item">
          <span class="sp-legend__dot sp-legend__dot--ok"></span> Operational
        </span>
        <span class="sp-legend__item">
          <span class="sp-legend__scale"></span> Partial outage
        </span>
        <span class="sp-legend__item">
          <span class="sp-legend__dot sp-legend__dot--down"></span> Down all day
        </span>
        <span class="sp-legend__item">
          <span class="sp-legend__dot sp-legend__dot--maintenance"></span> Maintenance
        </span>
        <span class="sp-legend__item">
          <span class="sp-legend__dot sp-legend__dot--nodata"></span> No data
        </span>
      </div>

      <!-- Incident history -->
      <section class="sp-incidents" data-testid="sp-incidents">
        <h2 class="sp-incidents__title">Incident history</h2>
        <p class="sp-incidents__subtitle">Outages detected in the last 90 days</p>

        <div *ngIf="!incidents.length" class="sp-incidents__empty">
          <span class="sp-incidents__empty-icon">✓</span> No incidents in the last 90 days.
        </div>

        <ol *ngIf="incidents.length" class="sp-timeline">
          <li *ngFor="let incident of visibleIncidents" class="sp-timeline__item"
            [class.sp-timeline__item--ongoing]="!incident.resolved_at">

            <div class="sp-timeline__times">
              <span class="sp-timeline__start">
                <span class="sp-timeline__date">
                  {{ incident.started_at | date:'MMM d' }}<sup>{{ dayOrdinal(incident.started_at) }}</sup>
                </span>
                <span class="sp-timeline__time">{{ incident.started_at | date:'shortTime' }}</span>
              </span>

              <span class="sp-timeline__end" *ngIf="incident.resolved_at">
                <ng-container *ngIf="!sameDay(incident.started_at, incident.resolved_at)">
                  <span class="sp-timeline__date">
                    {{ incident.resolved_at | date:'MMM d' }}<sup>{{ dayOrdinal(incident.resolved_at) }}</sup>
                  </span>
                </ng-container>
                <span class="sp-timeline__time">{{ incident.resolved_at | date:'shortTime' }}</span>
              </span>
              <span class="sp-timeline__end sp-timeline__now" *ngIf="!incident.resolved_at">Now</span>
            </div>

            <div class="sp-timeline__rail" aria-hidden="true">
              <span class="sp-timeline__node"></span>
              <span class="sp-timeline__line"></span>
              <span class="sp-timeline__node"></span>
            </div>

            <article class="sp-timeline__card">
              <h3 class="sp-timeline__card-title">{{ incident.component }} outage</h3>
              <p class="sp-timeline__card-text" *ngIf="incident.resolved_at">
                {{ incident.component }} was unreachable for {{ duration(incident) }}.
              </p>
              <p class="sp-timeline__card-text" *ngIf="!incident.resolved_at">
                {{ incident.component }} has been unreachable for {{ duration(incident) }}.
              </p>
              <p class="sp-timeline__card-status">
                - {{ incident.resolved_at ? 'Resolved' : 'Ongoing' }} -
              </p>
            </article>
          </li>
        </ol>

        <button *ngIf="hiddenIncidentCount > 0 || showAllIncidents && incidents.length > 5" type="button"
          class="sp-timeline__more" (click)="showAllIncidents = !showAllIncidents"
          [attr.aria-label]="showAllIncidents ? 'Show fewer incidents' : 'Show ' + hiddenIncidentCount + ' more incidents'">
          <svg [class.sp-timeline__more-icon--up]="showAllIncidents" class="sp-timeline__more-icon"
            width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="6 6 12 12 18 6"></polyline>
            <polyline points="6 12 12 18 18 12"></polyline>
          </svg>
          <span>{{ showAllIncidents ? 'Show less' : hiddenIncidentCount + ' more' }}</span>
        </button>
      </section>

    </main>

    <!-- Footer -->
    <footer class="sp-footer">
      <div class="sp-container sp-footer__inner">
        <span>Powered by <a routerLink="/" class="sp-footer-link">PulseWatch</a></span>
      </div>
    </footer>

  </ng-container>

  <!-- 404 -->
  <div *ngIf="!loading && !statusPage" class="sp-empty">
    <p>Status page not found.</p>
  </div>

</div>
